import type { ReactNode } from "react";

/**
 * Deliberately tiny, safe subset of Markdown (headings, bold, lists,
 * paragraphs, italic-note) rendered as real React elements — never
 * dangerouslySetInnerHTML, so admin-authored legal content can't inject
 * arbitrary HTML/scripts into any of the 4 apps that render it.
 */
function renderInline(text: string, keyPrefix: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
      : <span key={`${keyPrefix}-${i}`}>{part}</span>,
  );
}

export function MarkdownLite({ content, className }: { content: string; className?: string }) {
  const blocks = content.split(/\n\n+/).filter((b) => b.trim().length > 0);
  return (
    <div className={className}>
      {blocks.map((block, i) => {
        const key = `b-${i}`;
        // a heading line may be followed directly (no blank line) by its text: the heading is the first line only
        const nl = block.indexOf("\n");
        const firstLine = nl < 0 ? block : block.slice(0, nl);
        const rest = nl < 0 ? "" : block.slice(nl + 1);
        if (firstLine.startsWith("## ") || firstLine.startsWith("# ")) {
          const h2 = firstLine.startsWith("# ") && !firstLine.startsWith("## ");
          return (
            <div key={key}>
              {h2
                ? <h2 className="mb-2 mt-5 text-lg font-black text-[#191c1d] first:mt-0">{firstLine.slice(2)}</h2>
                : <h3 className="mb-1.5 mt-4 text-base font-black text-[#191c1d] first:mt-0">{firstLine.slice(3)}</h3>}
              {rest.trim() && <MarkdownLite content={rest} />}
            </div>
          );
        }
        if (block.startsWith("_") && block.endsWith("_")) {
          return <p key={key} className="mb-3 text-sm italic text-[#737373]">{block.slice(1, -1)}</p>;
        }
        const lines = block.split("\n");
        if (lines.every((l) => l.startsWith("- "))) {
          return (
            <ul key={key} className="mb-3 list-disc space-y-1 pl-5 text-sm text-[#404040]">
              {lines.map((l, j) => <li key={j}>{renderInline(l.slice(2), `${key}-${j}`)}</li>)}
            </ul>
          );
        }
        return <p key={key} className="mb-3 whitespace-pre-wrap text-sm leading-relaxed text-[#404040]">{renderInline(block, key)}</p>;
      })}
    </div>
  );
}
